import AgencyFormsTab from "./AgencyFormsTab";
import { XMarkIcon, PlusIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Button from "../common/Button";
import Input from "../common/Input";
import PreferencesForm from "../Settings/PreferencesForm";
import SnippetManager from "../Agency/SnippetManager";
import ReportTemplateManager, {
  ReportTemplateManagerHandle,
} from "../Settings/ReportTemplates/ReportTemplateManager";
import { getEvaluation } from "./evaluation";
import DeleteAgencyPanel from "./DeleteAgencyPanel";
import ArchiveAgencyPanel from "./ArchiveAgencyPanel";
import FindingsSurveyOfficerPicker from "./FindingsSurveyOfficerPicker";
import AddonSeatsSection, { AddonSeatsSectionHandle } from "./AddonSeatsSection";

type Agency = {
  id: string;
  name: string;
  email: string;
  city: string | null;
  state: string | null;
  max_users: number;
  sworn_officers: number | null;
  officer_count?: number;
  trial_seats: number | null;
  trial_expires_at: string | null;
  trial_code: string | null;
  findings_generated_at: string | null;
  findings_slug: string | null;
  findings_type: "trial" | "annual" | null;
  archived_at?: string | null;
  created_at: Date;
  subscription: {
    current_period_end: string;
    status: string;
    platform_subscription_id: string | null;
  } | null;
};

// Returned by GET /findings-jobs/:id while the worker is running.
type FindingsJobStatus = {
  id: string;
  agency_id: string;
  type: "trial" | "annual";
  status: "pending" | "running" | "done" | "failed";
  progress: string | null;
  error: string | null;
  result_slug: string | null;
  result_summary:
    | {
        cases: number;
        hours_saved: number;
        top_officer: string | null;
        generated_at: string;
        salary?: {
          salary: number;
          label: string;
          source: string;
          source_url?: string;
          source_date?: string;
          confidence: "high" | "medium" | "low";
        };
      }
    | null;
  page_url: string | null;
  created_at: string;
  updated_at: string;
};

interface PendingInvitation {
  id: string;
  new_owner_email: string;
  expires_at: string;
  created_at: string;
}

interface AgencyAdmin {
  id: string;
  name: string | null;
  email: string;
  created_at: string;
}

interface PendingAdminInvitation {
  id: string;
  email: string;
  expires_at: string;
  created_at: string;
}

interface EditAgencyModalProps {
  modalRef: React.RefObject<HTMLDialogElement>;
  agency: Agency | null;
  onSave: (
    agencyId: string,
    maxUsers: number,
    subscriptionEnd: string | null,
    status: string | null,
    originalMaxUsers: number,
    originalSubscriptionEnd: string | null,
    originalStatus: string | null
  ) => Promise<void>;
  onRefresh: () => Promise<void>;
  adminEmail: string;
}

const EditAgencyModal: React.FC<EditAgencyModalProps> = ({
  modalRef,
  agency,
  onSave,
  onRefresh,
  adminEmail,
}) => {
  const [activeTab, setActiveTab] = useState<
    "subscription" | "transfer" | "admins" | "integrations" | "report_preferences" | "report_templates" | "forms" | "snippets" | "trial_findings" | "danger"
  >("subscription");
  const templateManagerRef = useRef<ReportTemplateManagerHandle>(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  // Findings job state — replaces the synchronous trial-findings result.
  // generatingTrialFindings now means "a job is queued or running" (button
  // disabled, progress card visible). trialFindingsResult is what the
  // worker wrote back into the findings_jobs row.
  const [generatingTrialFindings, setGeneratingTrialFindings] = useState(false);
  const [trialFindingsResult, setTrialFindingsResult] = useState<FindingsJobStatus | null>(
    null
  );
  const [trialFindingsError, setTrialFindingsError] = useState<string | null>(null);
  // Picked at generation time; defaults are derived from the agency's
  // current state when the modal opens.
  const [findingsType, setFindingsType] = useState<"trial" | "annual">("trial");
  const [obscureAudioSplit, setObscureAudioSplit] = useState(false);
  // Annual reports require a window. Default to last 12 months on mount.
  const [windowStart, setWindowStart] = useState<string>("");
  const [windowEnd, setWindowEnd] = useState<string>("");
  // Optional scoping: when set, the report is computed from only the top N
  // officers by time saved. Empty string = all officers (agency-wide).
  const [topUsers, setTopUsers] = useState<string>("");
  // Optional: build only the report's officer survey section from
  // hand-picked respondents. Off = every officer who answered.
  const [surveyOfficersOnly, setSurveyOfficersOnly] = useState(false);
  const [surveyOfficerIds, setSurveyOfficerIds] = useState<string[]>([]);
  const [maxUsers, setMaxUsers] = useState<number>(0);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string>("");
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>("");
  const [swornOfficers, setSwornOfficers] = useState<string>("");
  // Add-on seats (Investigations, Footage analysis). Agency-level like Max
  // Seats; per-user grants live in the users modal. Set here as comp/trial/
  // invoiced only — paid seats are written by the Stripe webhook. Each
  // section stages its edits and Save writes them through the ref.
  const invSeatsRef = useRef<AddonSeatsSectionHandle>(null);
  const videoSeatsRef = useRef<AddonSeatsSectionHandle>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);

  // Store original values to detect changes
  const [originalMaxUsers, setOriginalMaxUsers] = useState<number>(0);
  const [originalSwornOfficers, setOriginalSwornOfficers] = useState<string>("");
  const [originalSubscriptionEnd, setOriginalSubscriptionEnd] = useState<string>("");
  const [originalSubscriptionStatus, setOriginalSubscriptionStatus] = useState<string>("");

  // Transfer state
  const [newOwnerEmail, setNewOwnerEmail] = useState("");
  const [pendingInvitation, setPendingInvitation] = useState<PendingInvitation | null>(null);
  const [isLoadingTransfer, setIsLoadingTransfer] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [showTransferConfirmation, setShowTransferConfirmation] = useState(false);
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  // Admin invite state
  const [agencyAdmins, setAgencyAdmins] = useState<AgencyAdmin[]>([]);
  const [pendingAdminInvitations, setPendingAdminInvitations] = useState<
    PendingAdminInvitation[]
  >([]);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);
  const [adminInviteEmail, setAdminInviteEmail] = useState("");
  const [isInvitingAdmin, setIsInvitingAdmin] = useState(false);
  const [adminInviteError, setAdminInviteError] = useState("");
  const [resendingAdminInviteId, setResendingAdminInviteId] = useState<string | null>(null);
  const [cancellingAdminInviteId, setCancellingAdminInviteId] = useState<string | null>(null);

  // Integration state
  const [integrationData, setIntegrationData] = useState<
    {
      vendor: string;
      vendorName: string;
      enabled: boolean;
      credentials: Record<string, string>;
      fields: { key: string; label: string; type: string; required: boolean; placeholder?: string; helpText?: string }[];
      last_verified_at: string | null;
    }[]
  >([]);
  const [vendors, setVendors] = useState<
    { id: string; name: string; description: string; fields: { key: string; label: string; type: string; required: boolean; placeholder?: string; helpText?: string }[] }[]
  >([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<string>("");
  const [editCredentials, setEditCredentials] = useState<Record<string, string>>({});
  const [editEnabled, setEditEnabled] = useState(false);
  const [isSavingIntegration, setIsSavingIntegration] = useState(false);
  const [isVerifyingIntegration, setIsVerifyingIntegration] = useState(false);
  const [isDeletingIntegration, setIsDeletingIntegration] = useState(false);
  const [confirmDeleteIntegration, setConfirmDeleteIntegration] = useState(false);
  const [integrationTestResult, setIntegrationTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (agency) {
      // Show the effective seat total: agencies in an active evaluation
      // period hold their seats in trial_seats, not max_users. The
      // max-users endpoint routes edits back to the right column.
      const evalState = getEvaluation(agency);
      const maxUsersValue =
        agency.max_users + (evalState?.active ? evalState.seats : 0);
      const subscriptionEndValue = agency.subscription?.current_period_end
        ? new Date(agency.subscription.current_period_end)
            .toISOString()
            .split("T")[0]
        : "";
      const subscriptionStatusValue = agency.subscription?.status || "";

      const swornOfficersValue = agency.sworn_officers != null ? String(agency.sworn_officers) : "";

      setMaxUsers(maxUsersValue);
      setSwornOfficers(swornOfficersValue);
      setSubscriptionEnd(subscriptionEndValue);
      setSubscriptionStatus(subscriptionStatusValue);

      // Store original values
      setOriginalMaxUsers(maxUsersValue);
      setOriginalSwornOfficers(swornOfficersValue);
      setOriginalSubscriptionEnd(subscriptionEndValue);
      setOriginalSubscriptionStatus(subscriptionStatusValue);

      // Fetch pending transfer
      setNewOwnerEmail("");
      fetchPendingInvitation(agency.id);
      fetchIntegrations(agency.id);

      // Admin invite state is per-agency
      setAdminInviteEmail("");
      setAdminInviteError("");
      fetchAgencyAdmins(agency.id);

      // Findings state is per-agency — clear out any previous result/
      // error from a different agency so the success panel doesn't bleed
      // through when switching between agencies without closing the modal.
      setTrialFindingsResult(null);
      setTrialFindingsError(null);
      setGeneratingTrialFindings(false);
      setObscureAudioSplit(false);
      setTopUsers("");
      setSurveyOfficersOnly(false);
      setSurveyOfficerIds([]);
      // Default report type: active subscription → annual review (the
      // agency is past their trial), otherwise → trial findings.
      const isActiveSub = agency.subscription?.status === "active";
      setFindingsType(isActiveSub ? "annual" : "trial");
      // Default to last 12 months for annual reports.
      const today = new Date();
      const yearAgo = new Date(today);
      yearAgo.setFullYear(today.getFullYear() - 1);
      const toIsoDate = (d: Date) => d.toISOString().split("T")[0];
      setWindowStart(toIsoDate(yearAgo));
      setWindowEnd(toIsoDate(today));
    }
  }, [agency]);

  const fetchIntegrations = async (agencyId: string) => {
    setIsLoadingIntegrations(true);
    try {
      const [integrationsRes, vendorsRes] = await Promise.all([
        axios.get(`/api/agency-management/agencies/${agencyId}/integrations`),
        axios.get(`/api/agency-management/vendors`),
      ]);
      setIntegrationData(integrationsRes.data);
      setVendors(vendorsRes.data);

      // Auto-select first configured integration
      if (integrationsRes.data.length > 0) {
        const first = integrationsRes.data[0];
        setSelectedVendor(first.vendor);
        setEditCredentials(first.credentials);
        setEditEnabled(first.enabled);
      } else {
        setSelectedVendor("");
        setEditCredentials({});
        setEditEnabled(false);
      }
      setConfirmDeleteIntegration(false);
      setIntegrationTestResult(null);
    } catch (error) {
      console.error("Error fetching integrations:", error);
      setIntegrationData([]);
    } finally {
      setIsLoadingIntegrations(false);
    }
  };

  const handleVendorSelect = (vendorId: string) => {
    setSelectedVendor(vendorId);
    setConfirmDeleteIntegration(false);
    setIntegrationTestResult(null);
    setVisibleFields(new Set());

    const existing = integrationData.find((i) => i.vendor === vendorId);
    if (existing) {
      setEditCredentials(existing.credentials);
      setEditEnabled(existing.enabled);
    } else {
      setEditCredentials({});
      setEditEnabled(false);
    }
  };

  const handleSaveIntegration = async () => {
    if (!agency) return;
    setIsSavingIntegration(true);
    try {
      await axios.post(
        `/api/agency-management/agencies/${agency.id}/integrations`,
        {
          vendor: selectedVendor,
          credentials: editCredentials,
          enabled: editEnabled,
        }
      );
      toast.success("Integration saved");
      await fetchIntegrations(agency.id);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to save integration");
    } finally {
      setIsSavingIntegration(false);
    }
  };

  const handleVerifyIntegration = async () => {
    if (!agency) return;
    setIsVerifyingIntegration(true);
    setIntegrationTestResult(null);
    try {
      await axios.post(
        `/api/agency-management/agencies/${agency.id}/integrations/${selectedVendor}/verify`,
        { credentials: editCredentials }
      );
      setIntegrationTestResult({ success: true });
      // Update last_verified_at locally instead of full refetch (which clears test result)
      setIntegrationData((prev) =>
        prev.map((i) =>
          i.vendor === selectedVendor
            ? { ...i, last_verified_at: new Date().toISOString() }
            : i
        )
      );
    } catch (error: any) {
      setIntegrationTestResult({
        success: false,
        error: error.response?.data?.error || "Verification failed",
      });
    } finally {
      setIsVerifyingIntegration(false);
    }
  };

  const handleDeleteIntegration = async () => {
    if (!agency) return;
    setIsDeletingIntegration(true);
    try {
      await axios.delete(
        `/api/agency-management/agencies/${agency.id}/integrations/${selectedVendor}`
      );
      toast.success("Integration removed");
      setConfirmDeleteIntegration(false);
      setEditCredentials({});
      setEditEnabled(false);
      setSelectedVendor("");
      await fetchIntegrations(agency.id);
    } catch (error) {
      toast.error("Failed to remove integration");
    } finally {
      setIsDeletingIntegration(false);
    }
  };

  const fetchAgencyAdmins = async (agencyId: string) => {
    setIsLoadingAdmins(true);
    try {
      const response = await axios.get(
        `/api/agency-management/agencies/${agencyId}/admins`
      );
      setAgencyAdmins(response.data.admins);
      setPendingAdminInvitations(response.data.pendingInvitations);
    } catch (error) {
      console.error("Error fetching agency admins:", error);
      setAgencyAdmins([]);
      setPendingAdminInvitations([]);
    } finally {
      setIsLoadingAdmins(false);
    }
  };

  const handleInviteAdmin = async () => {
    if (!agency || !adminInviteEmail.trim()) return;
    setIsInvitingAdmin(true);
    setAdminInviteError("");
    try {
      await axios.post(
        `/api/agency-management/agencies/${agency.id}/invite-admin`,
        { email: adminInviteEmail.trim(), adminEmail }
      );
      toast.success(`Admin invitation sent to ${adminInviteEmail.trim()}`);
      setAdminInviteEmail("");
      await fetchAgencyAdmins(agency.id);
    } catch (error: any) {
      setAdminInviteError(
        error.response?.data?.error || "Failed to send admin invitation"
      );
    } finally {
      setIsInvitingAdmin(false);
    }
  };

  const handleResendAdminInvite = async (invitationId: string, email: string) => {
    if (!agency) return;
    setResendingAdminInviteId(invitationId);
    try {
      await axios.post(
        `/api/agency-management/agencies/${agency.id}/resend-admin-invitation`,
        { invitationId, adminEmail }
      );
      toast.success(`Invitation resent to ${email}`);
      await fetchAgencyAdmins(agency.id);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to resend invitation");
    } finally {
      setResendingAdminInviteId(null);
    }
  };

  const handleCancelAdminInvite = async (invitationId: string, email: string) => {
    if (!agency) return;
    setCancellingAdminInviteId(invitationId);
    try {
      await axios.post(
        `/api/agency-management/agencies/${agency.id}/cancel-admin-invitation`,
        { invitationId, adminEmail }
      );
      toast.success(`Invitation to ${email} cancelled`);
      await fetchAgencyAdmins(agency.id);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to cancel invitation");
    } finally {
      setCancellingAdminInviteId(null);
    }
  };

  const fetchPendingInvitation = async (agencyId: string) => {
    try {
      const response = await axios.get(
        `/api/agency-management/agencies/${agencyId}/pending-transfer`
      );
      setPendingInvitation(response.data.invitation);
    } catch (error) {
      console.error("Error fetching pending invitation:", error);
    }
  };

  const handleSendTransfer = async () => {
    if (!agency) return;
    setIsLoadingTransfer(true);
    setShowTransferConfirmation(false);

    try {
      await axios.post(
        `/api/agency-management/agencies/${agency.id}/send-transfer-invitation`,
        { newOwnerEmail, adminEmail }
      );
      setTransferError("");
      setNewOwnerEmail("");
      await fetchPendingInvitation(agency.id);
    } catch (error: any) {
      setTransferError(error.response?.data?.error || "Failed to send transfer invitation");
    } finally {
      setIsLoadingTransfer(false);
    }
  };

  const handleResendInvitation = async () => {
    if (!agency) return;
    setIsResending(true);
    setShowResendConfirmation(false);

    try {
      const response = await axios.post(
        `/api/agency-management/agencies/${agency.id}/resend-transfer-invitation`,
        { adminEmail }
      );
      toast.success(response.data.message);
      await fetchPendingInvitation(agency.id);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to resend invitation");
    } finally {
      setIsResending(false);
    }
  };

  const handleCancelInvitation = async () => {
    if (!agency) return;
    setIsCancelling(true);
    setShowCancelConfirmation(false);

    try {
      const response = await axios.post(
        `/api/agency-management/agencies/${agency.id}/cancel-transfer-invitation`,
        { adminEmail }
      );
      toast.success(response.data.message);
      await fetchPendingInvitation(agency.id);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to cancel invitation");
    } finally {
      setIsCancelling(false);
    }
  };

  const formatTransferDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleSave = async () => {
    if (!agency) return;

    setIsSaving(true);
    try {
      // Update sworn officers if changed
      if (swornOfficers !== originalSwornOfficers) {
        await axios.patch(`/api/agency-management/agencies/${agency.id}/sworn-officers`, {
          sworn_officers: swornOfficers ? parseInt(swornOfficers) : 0,
          adminEmail,
        });
        await onRefresh();
      }

      // Add-on seats (no-ops when nothing changed)
      await invSeatsRef.current?.save();
      await videoSeatsRef.current?.save();

      await onSave(
        agency.id,
        maxUsers,
        subscriptionEnd || null,
        subscriptionStatus || null,
        originalMaxUsers,
        originalSubscriptionEnd || null,
        originalSubscriptionStatus || null
      );
      modalRef.current?.close();
    } catch (error) {
      console.error("Error saving agency:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!agency) return;

    setIsCreatingSubscription(true);
    try {
      await axios.post(`/api/agency-management/agencies/${agency.id}/subscription`, {
        adminEmail: adminEmail,
        max_users: maxUsers,
      });
      // Refresh the data and close the modal
      await onRefresh();
      modalRef.current?.close();
    } catch (error) {
      console.error("Error creating subscription:", error);
      if (axios.isAxiosError(error) && error.response) {
        alert(error.response.data.error || "Failed to create subscription");
      } else {
        alert("Failed to create subscription");
      }
    } finally {
      setIsCreatingSubscription(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatStatus = (status: string) => {
    switch (status) {
      case "trialing":
        return "Trialing";
      case "active":
        return "Active";
      case "incomplete_expired":
        return "Expired";
      default:
        return status;
    }
  };

  if (!agency) return null;

  const evaluation = getEvaluation(agency);

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Edit Agency</h3>
          <button
            onClick={() => modalRef.current?.close()}
            className="btn btn-sm btn-circle btn-ghost"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Agency Information */}
        <div className="bg-base-200 p-4 rounded-lg mb-4">
          <h4 className="font-semibold text-gray-700 dark:text-base-content/80 mb-3">
            Agency Information
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-base-content/60">Name</p>
              <p className="font-medium">{agency.name}</p>
            </div>
            <div>
              <p className="text-sm text-base-content/60">Email</p>
              <p className="font-medium">{agency.email}</p>
            </div>
            <div>
              <p className="text-sm text-base-content/60">Location</p>
              <p className="font-medium">
                {agency.city && agency.state
                  ? `${agency.city}, ${agency.state}`
                  : agency.city || agency.state || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-sm text-base-content/60">Subscription Status</p>
              {agency.subscription ? (
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    agency.subscription.status === "active"
                      ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400"
                      : agency.subscription.status === "trialing"
                      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300"
                      : "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400"
                  }`}
                >
                  {formatStatus(agency.subscription.status)}
                </span>
              ) : evaluation ? (
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    evaluation.active
                      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300"
                      : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400"
                  }`}
                >
                  {evaluation.active ? "Evaluation" : "Evaluation Ended"}
                </span>
              ) : (
                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 dark:bg-base-200 text-gray-800 dark:text-base-content/80">
                  No Subscription
                </span>
              )}
            </div>
            {evaluation && (
              <div className="col-span-2">
                <p className="text-sm text-base-content/60">Evaluation Period</p>
                <p className="font-medium text-sm">
                  {evaluation.seats} seats
                  {agency.trial_code
                    ? ` under code ${agency.trial_code}`
                    : ""}{" "}
                  — {evaluation.active ? "ends" : "ended"}{" "}
                  {formatDate(agency.trial_expires_at)}
                </p>
              </div>
            )}
            {agency.subscription && agency.subscription.platform_subscription_id && (
              <div className="col-span-2">
                <p className="text-sm text-base-content/60">Platform Subscription ID</p>
                <p className="font-medium text-sm break-all">
                  {agency.subscription.platform_subscription_id}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6 border-b border-base-300 overflow-x-auto">
          <nav className="flex gap-0 -mb-px min-w-max">
            {(
              [
                { key: "subscription", label: "Subscription" },
                { key: "transfer", label: "Transfer Agency Ownership" },
                { key: "admins", label: "Invite Admins" },
                { key: "integrations", label: "Evidence Integrations" },
                { key: "report_preferences", label: "Report Preferences" },
                { key: "report_templates", label: "Report Templates" },
                { key: "forms", label: "Forms" },
                { key: "snippets", label: "Snippets" },
                {
                  key: "trial_findings",
                  // Tab label reflects what's most likely for this agency:
                  // the persisted findings_type wins, then subscription
                  // status as a fallback. Either way both report types
                  // remain selectable from inside the tab.
                  label:
                    (agency?.findings_type === "annual"
                      ? "Department Findings"
                      : agency?.findings_type === "trial"
                      ? "Trial Findings"
                      : agency?.subscription?.status === "active"
                      ? "Department Findings"
                      : "Trial Findings"),
                },
                { key: "danger", label: "Archive / Delete" },
              ] as { key: typeof activeTab; label: string }[]
            ).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? tab.key === "danger"
                      ? "border-red-500 border-b-[3px] text-red-600 dark:text-red-400 font-semibold"
                      : "border-blue-500 border-b-[3px] text-blue-600 dark:text-blue-300 font-semibold"
                    : tab.key === "danger"
                      ? "border-transparent text-red-600/70 dark:text-red-400/70 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-800"
                      : "border-transparent text-base-content/60 hover:text-gray-700 dark:hover:text-base-content/80 hover:border-gray-300 dark:hover:border-base-content/30"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Subscription Tab */}
        {activeTab === "subscription" && (
          <>
            <div className="space-y-4">
              {/* Max Users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                  Max Users (Seats)
                </label>
                <input
                  type="number"
                  min="0"
                  value={maxUsers}
                  onChange={(e) => setMaxUsers(parseInt(e.target.value) || 0)}
                  className="input input-bordered w-full"
                />
                {evaluation?.active ? (
                  <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                    Includes {evaluation.seats} evaluation seats — changes
                    adjust the evaluation seat count, which expires{" "}
                    {formatDate(agency.trial_expires_at)}
                  </p>
                ) : (
                  <p className="text-xs text-base-content/60 mt-1">
                    Number of users allowed for this agency
                  </p>
                )}
              </div>

              {/* Sworn Officers */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                  Sworn Officers
                </label>
                <input
                  type="number"
                  min="0"
                  value={swornOfficers}
                  onChange={(e) => setSwornOfficers(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  className="input input-bordered w-full"
                  placeholder="# of sworn officers"
                />
                <p className="text-xs text-base-content/60 mt-1">
                  Number of sworn officers in this agency
                </p>
              </div>

              {/* Add-on seats: Investigations and Footage analysis */}
              <AddonSeatsSection ref={invSeatsRef} agencyId={agency.id} adminEmail={adminEmail} addon="investigations" />
              <AddonSeatsSection ref={videoSeatsRef} agencyId={agency.id} adminEmail={adminEmail} addon="video" />

              {/* Subscription Section */}
              {agency.subscription ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                      Subscription Status
                    </label>
                    <select
                      value={subscriptionStatus}
                      onChange={(e) => setSubscriptionStatus(e.target.value)}
                      className="select select-bordered w-full"
                    >
                      <option value="active">Active</option>
                      <option value="trialing">Trialing</option>
                      <option value="incomplete_expired">Incomplete Expired</option>
                    </select>
                    <p className="text-xs text-base-content/60 mt-1">
                      Current: {formatStatus(agency.subscription.status)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                      Subscription End Date
                    </label>
                    <input
                      type="date"
                      value={subscriptionEnd}
                      onChange={(e) => setSubscriptionEnd(e.target.value)}
                      className="input input-bordered w-full"
                    />
                    <p className="text-xs text-base-content/60 mt-1">
                      Current: {formatDate(agency.subscription.current_period_end)}
                    </p>
                  </div>
                </>
              ) : evaluation?.active ? (
                <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-1">
                        Evaluation Period Active
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                        This agency is evaluating {evaluation.seats} seats
                        {agency.trial_code
                          ? ` under code ${agency.trial_code}`
                          : ""}{" "}
                        until {formatDate(agency.trial_expires_at)}. No
                        subscription is needed while the evaluation runs.
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-300 mb-3">
                        When this agency is ready to purchase, create a
                        subscription below — it takes precedence over the
                        evaluation. It starts as a 1-month trialing
                        subscription you can then edit to Active with the
                        correct end date and seat count.
                      </p>
                      <Button
                        onClick={handleCreateSubscription}
                        variant="secondary"
                        className="w-full"
                        disabled={isCreatingSubscription}
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        {isCreatingSubscription
                          ? "Creating..."
                          : "Convert to Subscription"}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-amber-900 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-yellow-800 dark:text-amber-400 mb-1">
                        No Subscription
                      </h4>
                      <p className="text-sm text-yellow-700 dark:text-amber-400 mb-3">
                        {evaluation
                          ? `This agency's evaluation period ended ${formatDate(
                              agency.trial_expires_at
                            )} and it has no subscription. Create a trial subscription to restore access.`
                          : "This agency does not have a subscription. Create a trial subscription to get started."}
                      </p>
                      <div className="text-xs text-yellow-600 dark:text-amber-400 space-y-1 mb-3">
                        <p>• Trial subscription will be created</p>
                        <p>• Status: Trialing</p>
                        <p>• Duration: 1 month</p>
                        <p>
                          • Platform ID:{" "}
                          {(() => {
                            let platformId = agency.name
                              .toUpperCase()
                              .replace(/[^A-Z0-9]/g, "_")
                              .replace(/_+/g, "_")
                              .replace(/^_|_$/g, "");

                            if (agency.state) {
                              const statePart = agency.state
                                .toUpperCase()
                                .replace(/[^A-Z0-9]/g, "_")
                                .replace(/_+/g, "_")
                                .replace(/^_|_$/g, "");
                              platformId += "_" + statePart;
                            }

                            return platformId + "_TEST";
                          })()}
                        </p>
                      </div>
                      <Button
                        onClick={handleCreateSubscription}
                        variant="primary"
                        className="w-full"
                        disabled={isCreatingSubscription}
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        {isCreatingSubscription
                          ? "Creating..."
                          : "Create Trial Subscription"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-action">
              <Button
                onClick={() => modalRef.current?.close()}
                variant="secondary"
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="primary"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </>
        )}

        {/* Transfer Tab */}
        {activeTab === "transfer" && (
          <div className="space-y-4">
            {/* Pending Invitation Display */}
            {pendingInvitation && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                  Pending Transfer Invitation
                </h5>
                <div className="space-y-1 text-sm text-blue-800 dark:text-blue-300 mb-3">
                  <p>
                    <span className="font-medium">Sent to:</span>{" "}
                    {pendingInvitation.new_owner_email}
                  </p>
                  <p>
                    <span className="font-medium">Expires:</span>{" "}
                    {formatTransferDate(pendingInvitation.expires_at)}
                  </p>
                  <p>
                    <span className="font-medium">Sent:</span>{" "}
                    {formatTransferDate(pendingInvitation.created_at)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowResendConfirmation(true)}
                    variant="primary"
                    size="sm"
                    disabled={isResending || isCancelling}
                  >
                    {isResending ? "Resending..." : "Resend"}
                  </Button>
                  <Button
                    onClick={() => setShowCancelConfirmation(true)}
                    variant="danger"
                    size="sm"
                    disabled={isResending || isCancelling}
                  >
                    {isCancelling ? "Cancelling..." : "Cancel Invitation"}
                  </Button>
                </div>
              </div>
            )}

            {/* Send New Transfer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                New Owner Email
              </label>
              <input
                type="email"
                value={newOwnerEmail}
                onChange={(e) => {
                  setNewOwnerEmail(e.target.value);
                  setTransferError("");
                }}
                className="input input-bordered w-full"
                placeholder="Enter email of new agency owner"
              />
              <p className="text-xs text-base-content/60 mt-1">
                Once the new owner accepts, they become the primary administrator
                and the current owner loses access. Invitation expires in 7 days.
              </p>
              {transferError && (
                <p className="text-xs text-red-500 mt-1">{transferError}</p>
              )}
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  if (!newOwnerEmail.trim()) {
                    toast.error("New owner email is required");
                    return;
                  }
                  setShowTransferConfirmation(true);
                }}
                variant="secondary"
                disabled={isLoadingTransfer || !newOwnerEmail.trim()}
              >
                {isLoadingTransfer ? "Sending..." : "Send Invitation"}
              </Button>
            </div>
          </div>
        )}

        {/* Invite Admins Tab */}
        {activeTab === "admins" && (
          <div className="space-y-4">
            {/* Invite Form */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
                Invite New Admin
              </h4>
              <p className="text-xs text-blue-800 dark:text-blue-300 mb-3">
                Admins can log in to the agency dashboard, send officer
                invitations, and edit agency preferences.
              </p>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Input
                    title="Email"
                    variant="sm"
                    type="email"
                    value={adminInviteEmail}
                    onChange={(e) => {
                      setAdminInviteEmail(e.target.value);
                      setAdminInviteError("");
                    }}
                    className="w-full !max-w-none !min-h-10"
                    placeholder="admin@example.com"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleInviteAdmin();
                    }}
                  />
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleInviteAdmin}
                  disabled={isInvitingAdmin || !adminInviteEmail.trim()}
                  loading={isInvitingAdmin}
                >
                  Send Invite
                </Button>
              </div>
              {adminInviteError && (
                <p className="text-xs text-red-500 mt-2">{adminInviteError}</p>
              )}
            </div>

            {isLoadingAdmins ? (
              <div className="flex justify-center py-8">
                <span className="loading loading-spinner loading-md"></span>
              </div>
            ) : (
              <>
                {/* Current Admins */}
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-base-content/80 mb-2">
                    Admins ({agencyAdmins.length})
                  </h4>
                  {agencyAdmins.length > 0 ? (
                    <table className="table">
                      <thead className="border-b border-base-300">
                        <tr>
                          <th>Email</th>
                          <th>Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agencyAdmins.map((admin, index) => (
                          <tr
                            key={admin.id}
                            className={index % 2 === 0 ? "bg-base-100" : "bg-base-200"}
                          >
                            <td className="border-b border-base-200 max-w-[300px] truncate">
                              {admin.email}
                              {admin.email === agency.email && (
                                <span className="ml-2 badge badge-sm badge-primary">
                                  Owner
                                </span>
                              )}
                            </td>
                            <td className="border-b border-base-200">
                              {formatDate(admin.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-sm text-base-content/60 py-2">
                      No admin accounts found for this agency.
                    </p>
                  )}
                </div>

                {/* Pending Admin Invitations */}
                {pendingAdminInvitations.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 dark:text-base-content/80 mb-2">
                      Pending Invitations ({pendingAdminInvitations.length})
                      {pendingAdminInvitations.filter(
                        (inv) => new Date(inv.expires_at) < new Date()
                      ).length > 0 && (
                        <span className="text-red-500 text-sm font-normal ml-2">
                          {
                            pendingAdminInvitations.filter(
                              (inv) => new Date(inv.expires_at) < new Date()
                            ).length
                          }{" "}
                          expired
                        </span>
                      )}
                    </h4>
                    <table className="table">
                      <thead className="border-b border-base-300">
                        <tr>
                          <th>Email</th>
                          <th>Invited</th>
                          <th>Expires</th>
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingAdminInvitations.map((invitation, index) => {
                          const isExpired =
                            new Date(invitation.expires_at) < new Date();
                          return (
                            <tr
                              key={invitation.id}
                              className={index % 2 === 0 ? "bg-base-100" : "bg-base-200"}
                            >
                              <td className="border-b border-base-200 max-w-[250px] truncate">
                                {invitation.email}
                              </td>
                              <td className="border-b border-base-200">
                                {formatDate(invitation.created_at)}
                              </td>
                              <td className="border-b border-base-200">
                                {isExpired ? (
                                  <span className="text-red-600 dark:text-red-400 font-medium">
                                    {formatDate(invitation.expires_at)} (Expired)
                                  </span>
                                ) : (
                                  formatDate(invitation.expires_at)
                                )}
                              </td>
                              <td className="border-b border-base-200 text-right">
                                <div className="flex gap-2 justify-end">
                                  <Button
                                    variant="primary"
                                    size="xs"
                                    onClick={() =>
                                      handleResendAdminInvite(
                                        invitation.id,
                                        invitation.email
                                      )
                                    }
                                    disabled={
                                      resendingAdminInviteId === invitation.id
                                    }
                                    loading={
                                      resendingAdminInviteId === invitation.id
                                    }
                                  >
                                    Resend
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="xs"
                                    onClick={() =>
                                      handleCancelAdminInvite(
                                        invitation.id,
                                        invitation.email
                                      )
                                    }
                                    disabled={
                                      cancellingAdminInviteId === invitation.id
                                    }
                                    loading={
                                      cancellingAdminInviteId === invitation.id
                                    }
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Integrations Tab */}
        {activeTab === "integrations" && (
          <div className="space-y-4">
            {isLoadingIntegrations ? (
              <div className="flex justify-center py-8">
                <span className="loading loading-spinner loading-md"></span>
              </div>
            ) : (
              <>
                {/* Vendor Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                    Evidence Vendor
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {vendors.map((vendor) => {
                      const isConfigured = integrationData.some(
                        (i) => i.vendor === vendor.id
                      );
                      return (
                        <div
                          key={vendor.id}
                          className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                            selectedVendor === vendor.id
                              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40"
                              : "border-base-300 hover:border-gray-300 dark:hover:border-base-content/30"
                          }`}
                          onClick={() => handleVendorSelect(vendor.id)}
                        >
                          <h4 className="font-medium text-base-content text-sm">
                            {vendor.name}
                          </h4>
                          <span className="text-xs text-base-content/60">
                            {isConfigured ? "Configured" : vendor.description}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Credential Fields */}
                {(() => {
                  const vendorDef = vendors.find((v) => v.id === selectedVendor);
                  const existingIntegration = integrationData.find(
                    (i) => i.vendor === selectedVendor
                  );
                  if (!vendorDef) return null;
                  return (
                    <>
                      <div className="space-y-4 p-4 bg-base-200 rounded-lg">
                        <h4 className="font-medium text-base-content">
                          {vendorDef.name} Configuration
                        </h4>
                        {vendorDef.fields.map((field) => (
                          <div key={field.key}>
                            <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-1">
                              {field.label}
                              {field.required && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                            </label>
                            <div className="relative">
                              <input
                                type={
                                  field.type === "password" &&
                                  !visibleFields.has(field.key)
                                    ? "password"
                                    : "text"
                                }
                                className="input input-bordered w-full input-sm pr-9"
                                value={editCredentials[field.key] || ""}
                                onChange={(e) =>
                                  setEditCredentials((prev) => ({
                                    ...prev,
                                    [field.key]: e.target.value,
                                  }))
                                }
                                placeholder={
                                  field.placeholder || `Enter ${field.label}`
                                }
                              />
                              {field.type === "password" && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setVisibleFields((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(field.key)) {
                                        next.delete(field.key);
                                      } else {
                                        next.add(field.key);
                                      }
                                      return next;
                                    })
                                  }
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70"
                                >
                                  {visibleFields.has(field.key) ? (
                                    <EyeSlashIcon className="h-4 w-4" />
                                  ) : (
                                    <EyeIcon className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                            </div>
                            {field.helpText && (
                              <p className="text-xs text-base-content/60 mt-1">
                                {field.helpText}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Enable toggle */}
                      <div className="flex items-center justify-between p-3 bg-base-200 rounded-lg">
                        <div>
                          <h4 className="font-medium text-base-content text-sm">
                            Enable Integration
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-base-content/70">
                            Activate the connection to {vendorDef.name}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          className="toggle toggle-sm border-2 border-gray-400 bg-gray-400 checked:border-blue-500 checked:bg-blue-500 [&:not(:checked)]:bg-gray-400 [&:after]:!bg-white"
                          checked={editEnabled}
                          onChange={() => setEditEnabled(!editEnabled)}
                        />
                      </div>

                      {/* Last verified */}
                      {existingIntegration?.last_verified_at && (
                        <p className="text-xs text-base-content/60">
                          Last verified:{" "}
                          {new Date(
                            existingIntegration.last_verified_at
                          ).toLocaleString()}
                        </p>
                      )}

                      {/* Test Result */}
                      {integrationTestResult && (
                        <div
                          className={`p-3 rounded-md ${
                            integrationTestResult.success
                              ? "bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900"
                              : "bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900"
                          }`}
                        >
                          {integrationTestResult.success ? (
                            <span className="text-green-800 dark:text-green-400 text-sm">
                              &#10003; Connection test successful!
                            </span>
                          ) : (
                            <span className="text-red-800 dark:text-red-400 text-sm">
                              &#10007; Test failed:{" "}
                              {integrationTestResult.error}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-3 border-t border-base-300">
                        {existingIntegration && (
                          <Button
                            onClick={handleVerifyIntegration}
                            variant="secondary"
                            size="sm"
                            disabled={isVerifyingIntegration}
                          >
                            {isVerifyingIntegration
                              ? "Testing..."
                              : "Test Connection"}
                          </Button>
                        )}
                        <Button
                          onClick={handleSaveIntegration}
                          variant="primary"
                          size="sm"
                          disabled={isSavingIntegration}
                        >
                          {isSavingIntegration
                            ? "Saving..."
                            : "Save Configuration"}
                        </Button>
                        {existingIntegration && (
                          <>
                            {confirmDeleteIntegration ? (
                              <div className="flex items-center gap-2 ml-auto">
                                <span className="text-xs text-base-content/60">
                                  Are you sure?
                                </span>
                                <Button
                                  onClick={handleDeleteIntegration}
                                  variant="danger"
                                  size="sm"
                                  disabled={isDeletingIntegration}
                                >
                                  {isDeletingIntegration
                                    ? "Removing..."
                                    : "Confirm"}
                                </Button>
                                <Button
                                  onClick={() =>
                                    setConfirmDeleteIntegration(false)
                                  }
                                  variant="secondary"
                                  size="sm"
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                onClick={() =>
                                  setConfirmDeleteIntegration(true)
                                }
                                variant="danger"
                                size="sm"
                                className="ml-auto"
                              >
                                Remove
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {/* Report Preferences Tab */}
        {activeTab === "report_preferences" && agency && (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-md p-3 text-sm text-blue-800 dark:text-blue-300">
              Manage report preferences for <strong>{agency.name}</strong>.
              These are applied as defaults for every officer in the agency;
              officers can override them in their individual preferences.
            </div>
            <PreferencesForm
              key={agency.id}
              entityName={agency.name}
              entityId={agency.id}
              entityType="agency"
              fetchEndpoint={`/api/agency-management/agencies/${agency.id}/report-preferences`}
              saveEndpoint={`/api/agency-management/agencies/${agency.id}/report-preferences`}
              customButtonText="Save Agency Preferences"
              hidePushIcons
            />
          </div>
        )}

        {/* Report Templates Tab */}
        {activeTab === "report_templates" && agency && (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-md p-3 text-sm text-blue-800 dark:text-blue-300">
              Manage report templates for <strong>{agency.name}</strong>. Templates
              you create here are visible to every officer in the agency. Use
              "Default for all" from a template's actions menu to make it the
              default for the whole agency.
            </div>
            <ReportTemplateManager
              key={agency.id}
              ref={templateManagerRef}
              apiBasePath={`/api/agency-management/agencies/${agency.id}/report-templates`}
            />
            <div className="flex justify-end pt-2 border-t border-base-300">
              <Button
                variant="primary"
                disabled={isSavingTemplate}
                onClick={async () => {
                  setIsSavingTemplate(true);
                  try {
                    await templateManagerRef.current?.save();
                    toast.success("Template saved");
                  } catch (error) {
                    console.error("Error saving template:", error);
                    toast.error("Failed to save template");
                  } finally {
                    setIsSavingTemplate(false);
                  }
                }}
              >
                {isSavingTemplate ? "Saving..." : "Save Template Changes"}
              </Button>
            </div>
          </div>
        )}

        {/* Forms Tab */}
        {activeTab === "forms" && agency && (
          <AgencyFormsTab agencyId={agency.id} agencyName={agency.name} adminEmail={adminEmail} />
        )}

        {/* Snippets Tab */}
        {activeTab === "snippets" && agency && (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-md p-3 text-sm text-blue-800 dark:text-blue-300">
              Manage department-wide snippets for <strong>{agency.name}</strong>.
              Snippets are reusable text blocks available to every officer in the
              agency. Use double braces (e.g.{" "}
              <code className="bg-blue-100 dark:bg-blue-900/60 px-1 rounded">{"{{placeholder}}"}</code>
              ) for fillable placeholders.
            </div>
            <SnippetManager
              key={agency.id}
              apiBasePath={`/api/agency-management/agencies/${agency.id}/snippets`}
              embedded
            />
          </div>
        )}

        {/* Findings Tab */}
        {activeTab === "trial_findings" && agency && (() => {
          const completedJob =
            trialFindingsResult?.status === "done" ? trialFindingsResult : null;
          // Prefer a just-completed job's result over the persisted agency
          // value so the status box updates immediately after generation.
          const lastGeneratedAt =
            completedJob?.result_summary?.generated_at ??
            agency.findings_generated_at;
          const lastSlug =
            completedJob?.result_slug ?? agency.findings_slug;
          const lastType = agency.findings_type ?? "trial";
          const jobActive =
            trialFindingsResult?.status === "pending" ||
            trialFindingsResult?.status === "running";

          const startJob = async () => {
            // Pre-flight validation for annual reports.
            if (findingsType === "annual") {
              if (!windowStart || !windowEnd) {
                toast.error("Pick a start and end date for the annual report.");
                return;
              }
              if (new Date(windowStart) >= new Date(windowEnd)) {
                toast.error("End date must be after start date.");
                return;
              }
            }
            // Validate the optional top-users scope before kicking off.
            const trimmedTopUsers = topUsers.trim();
            if (trimmedTopUsers !== "") {
              const parsed = Number(trimmedTopUsers);
              if (!Number.isInteger(parsed) || parsed < 1) {
                toast.error("Top users must be a whole number of 1 or more.");
                return;
              }
            }
            if (surveyOfficersOnly && surveyOfficerIds.length === 0) {
              toast.error("Pick at least one officer for the survey section, or uncheck “Only show certain officers”.");
              return;
            }
            setGeneratingTrialFindings(true);
            setTrialFindingsResult(null);
            setTrialFindingsError(null);
            try {
              const startRes = await axios.post<{ jobId: string }>(
                `${process.env.REACT_APP_BACKEND_URL}/api/agency-management/agencies/${agency.id}/generate-findings`,
                {
                  adminEmail,
                  type: findingsType,
                  obscure_audio_split: obscureAudioSplit,
                  // Date strings are sent as YYYY-MM-DD; the server parses
                  // them as midnight UTC which is fine for date-range
                  // scoping at day granularity.
                  window_start: findingsType === "annual" ? windowStart : null,
                  window_end: findingsType === "annual" ? windowEnd : null,
                  // null = agency-wide; otherwise scope to the top N officers.
                  top_users:
                    trimmedTopUsers === "" ? null : Number(trimmedTopUsers),
                  // null = every officer who answered the survey; otherwise
                  // only these officers feed the survey section.
                  survey_user_ids: surveyOfficersOnly ? surveyOfficerIds : null,
                }
              );
              const jobId = startRes.data.jobId;
              // Poll every 2s until done | failed. The job runs in the
              // background on the server so we just keep reading status.
              // Cap total wait at 15 minutes — well above what any real
              // generation should take, but short enough to surface
              // hangs rather than spinning forever.
              const maxAttempts = (15 * 60) / 2;
              let job: FindingsJobStatus | null = null;
              for (let i = 0; i < maxAttempts; i++) {
                await new Promise((r) => setTimeout(r, 2000));
                const pollRes = await axios.get<FindingsJobStatus>(
                  `${process.env.REACT_APP_BACKEND_URL}/api/agency-management/findings-jobs/${jobId}`
                );
                job = pollRes.data;
                setTrialFindingsResult(job);
                if (job.status === "done" || job.status === "failed") break;
              }
              if (!job || job.status === "failed") {
                const msg = job?.error || "Generation failed";
                setTrialFindingsError(msg);
                toast.error(msg);
              } else if (job.status === "done") {
                toast.success(
                  findingsType === "annual"
                    ? "Department findings generated"
                    : "Trial findings generated"
                );
                await onRefresh();
              } else {
                const msg = "Generation timed out — check job status later.";
                setTrialFindingsError(msg);
                toast.error(msg);
              }
            } catch (err: any) {
              const msg =
                err?.response?.data?.error ||
                err?.message ||
                "Generation failed";
              setTrialFindingsError(msg);
              toast.error(msg);
            } finally {
              setGeneratingTrialFindings(false);
            }
          };

          return (
            <div className="space-y-4">
              {/* Status line — shows the most-recent generation. Findings
                  share one URL per agency; regenerating replaces the
                  previous report regardless of type. */}
              <div className="rounded-md border border-base-300 bg-base-200 px-4 py-3">
                {lastGeneratedAt && lastSlug ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm text-gray-700 dark:text-base-content/80">
                      <div>
                        Last generated:{" "}
                        <span className="font-semibold">
                          {new Date(lastGeneratedAt).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <div className="text-xs text-base-content/60 mt-0.5">
                        Type:{" "}
                        <span className="font-semibold">
                          {lastType === "annual" ? "Department review" : "Trial findings"}
                        </span>
                      </div>
                    </div>
                    <a
                      href={`${
                        lastType === "annual" ? "/department-review" : "/trial-findings"
                      }/${lastSlug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 hover:underline whitespace-nowrap"
                    >
                      View page ↗
                    </a>
                  </div>
                ) : (
                  <div className="text-sm text-base-content/60">Never generated.</div>
                )}
              </div>

              {/* Report type — picks trial vs annual. The annual variant
                  also reveals a date-range picker below. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                  Report type
                </label>
                <select
                  value={findingsType}
                  onChange={(e) =>
                    setFindingsType(e.target.value === "annual" ? "annual" : "trial")
                  }
                  className="select select-bordered w-full"
                  disabled={generatingTrialFindings}
                >
                  <option value="trial">Trial findings — short-window pilot diagnostic</option>
                  <option value="annual">Department review — retrospective over a date range</option>
                </select>
                <p className="text-xs text-base-content/60 mt-1 leading-relaxed">
                  {findingsType === "annual"
                    ? "Generates a retrospective report covering the date range below. Includes the same time-savings math but framed as “already recovered” instead of a pilot projection. Best for active departments after their first year of use."
                    : "Generates the original post-trial diagnostic — short window, auto-detected first/last case, cost projection framed as forward-looking. Best for pilots and early evaluations."}
                </p>
              </div>

              {/* Date range — only shown for annual reports. */}
              {findingsType === "annual" && (
                <div className="rounded-md border border-base-300 bg-base-200 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-2">
                    Review window
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="block text-xs text-gray-700 dark:text-base-content/80 mb-1">Start</span>
                      <input
                        type="date"
                        value={windowStart}
                        onChange={(e) => setWindowStart(e.target.value)}
                        className="input input-bordered input-sm w-full"
                        disabled={generatingTrialFindings}
                      />
                    </label>
                    <label className="block">
                      <span className="block text-xs text-gray-700 dark:text-base-content/80 mb-1">End</span>
                      <input
                        type="date"
                        value={windowEnd}
                        onChange={(e) => setWindowEnd(e.target.value)}
                        className="input input-bordered input-sm w-full"
                        disabled={generatingTrialFindings}
                      />
                    </label>
                  </div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        const today = new Date();
                        const ago = new Date(today);
                        ago.setFullYear(today.getFullYear() - 1);
                        setWindowStart(ago.toISOString().split("T")[0]);
                        setWindowEnd(today.toISOString().split("T")[0]);
                      }}
                      disabled={generatingTrialFindings}
                      className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 underline disabled:opacity-50"
                    >
                      Last 12 months
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const today = new Date();
                        const y = today.getFullYear();
                        setWindowStart(`${y}-01-01`);
                        setWindowEnd(today.toISOString().split("T")[0]);
                      }}
                      disabled={generatingTrialFindings}
                      className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 underline disabled:opacity-50"
                    >
                      YTD
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const y = new Date().getFullYear() - 1;
                        setWindowStart(`${y}-01-01`);
                        setWindowEnd(`${y}-12-31`);
                      }}
                      disabled={generatingTrialFindings}
                      className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 underline disabled:opacity-50"
                    >
                      Last calendar year
                    </button>
                  </div>
                </div>
              )}

              {/* Report variant — Detailed vs Brief (obscure audio split). */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                  Report variant
                </label>
                <select
                  value={obscureAudioSplit ? "brief" : "detailed"}
                  onChange={(e) => setObscureAudioSplit(e.target.value === "brief")}
                  className="select select-bordered w-full"
                  disabled={generatingTrialFindings}
                >
                  <option value="detailed">Detailed — full workflow breakdown</option>
                  <option value="brief">Brief — combine audio workflows</option>
                </select>
                <p className="text-xs text-base-content/60 mt-1 leading-relaxed">
                  {obscureAudioSplit
                    ? "Combines narrative dictation and interaction audio into a single “audio captured” figure. Hides workflow type labels in the case index and the per-workflow breakdown in §1.2 and §2.1. Hours saved is unchanged."
                    : "Shows the original breakdown: narrative dictation vs. interaction audio in §1.2 and §2.1, and workflow labels in the case index."}
                </p>
              </div>

              {/* Officer scope — optionally restrict the whole report to the
                  top N officers by time saved. Blank = every officer counts. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                  Officer scope
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={topUsers}
                  onChange={(e) =>
                    setTopUsers(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  className="input input-bordered w-full"
                  placeholder="All officers"
                  disabled={generatingTrialFindings}
                />
                <p className="text-xs text-base-content/60 mt-1 leading-relaxed">
                  {topUsers.trim() === ""
                    ? "Leave blank to include every officer in the agency. The report's case counts, hours saved, and cost projection cover the whole department."
                    : `Scopes the entire report to the top ${topUsers.trim()} officer${
                        topUsers.trim() === "1" ? "" : "s"
                      } by time saved — case counts, hours saved, the leaderboard, and the per-officer pace behind the cost projection are all computed from just these officers. Use this to base findings on the officers who actually engaged during a trial.`}
                </p>
              </div>

              {/* Survey scope — optionally build the officer survey section
                  from hand-picked respondents. Nothing else is scoped. */}
              <FindingsSurveyOfficerPicker
                agencyId={agency.id}
                windowStart={findingsType === "annual" && windowStart ? windowStart : null}
                enabled={surveyOfficersOnly}
                onEnabledChange={setSurveyOfficersOnly}
                selected={surveyOfficerIds}
                onSelectedChange={setSurveyOfficerIds}
                disabled={generatingTrialFindings}
              />

              {/* Generate button */}
              <div>
                <Button
                  onClick={startJob}
                  variant="primary"
                  disabled={generatingTrialFindings || !agency.sworn_officers}
                  className="w-full"
                >
                  {generatingTrialFindings
                    ? "Generating…"
                    : lastGeneratedAt
                    ? findingsType === "annual"
                      ? "Regenerate department review"
                      : "Regenerate trial findings"
                    : findingsType === "annual"
                    ? "Generate department review"
                    : "Generate trial analysis"}
                </Button>
                {!agency.sworn_officers && (
                  <div className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                    Set sworn officer count on the Subscription tab first — the cost
                    projection needs a real headcount.
                  </div>
                )}
                <div className="text-xs text-base-content/60 mt-2 leading-relaxed">
                  Generation runs in the background against the live database (read-only)
                  and overwrites the existing JSON in S3 if one exists. The public page URL
                  stays the same so you can keep sharing it. Annual reports over large
                  windows can take several minutes — leave the modal open or come back
                  later; the job will keep running.
                </div>
              </div>

              {/* Live progress while a job is running */}
              {jobActive && (
                <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/40 p-4">
                  <div className="flex items-center gap-3">
                    <div className="loading loading-spinner loading-sm text-blue-700 dark:text-blue-300" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                        {trialFindingsResult?.status === "pending"
                          ? "Queued…"
                          : trialFindingsResult?.progress || "Running…"}
                      </div>
                      <div className="text-xs text-blue-700/80 dark:text-blue-300/80 mt-0.5">
                        Job ID:{" "}
                        <span className="font-mono">{trialFindingsResult?.id}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Success callout — only when the job is done */}
              {completedJob?.result_summary && (
                <div className="rounded-md border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 p-4">
                  <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-400 mb-2">
                    ✓ Generated successfully
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-emerald-900 dark:text-emerald-300 mb-3">
                    <div>
                      <div className="text-emerald-700/70 dark:text-emerald-400/70 uppercase tracking-wider text-[10px] font-semibold">
                        AI-assisted cases
                      </div>
                      <div className="text-base font-semibold">
                        {completedJob.result_summary.cases.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-emerald-700/70 dark:text-emerald-400/70 uppercase tracking-wider text-[10px] font-semibold">
                        Hours saved
                      </div>
                      <div className="text-base font-semibold">
                        {completedJob.result_summary.hours_saved}
                      </div>
                    </div>
                    {completedJob.result_summary.top_officer && (
                      <div className="col-span-2">
                        <div className="text-emerald-700/70 dark:text-emerald-400/70 uppercase tracking-wider text-[10px] font-semibold">
                          Top officer
                        </div>
                        <div className="text-base font-semibold">
                          {completedJob.result_summary.top_officer}
                        </div>
                      </div>
                    )}
                  </div>
                  {completedJob.result_summary.salary && (
                    <div className="border-t border-emerald-200 dark:border-emerald-900 pt-3 mt-3">
                      <div className="text-emerald-700/70 dark:text-emerald-400/70 uppercase tracking-wider text-[10px] font-semibold mb-1">
                        Salary basis (LLM-sourced)
                      </div>
                      <div className="text-sm text-emerald-900 dark:text-emerald-300">
                        <span className="font-semibold">
                          ${completedJob.result_summary.salary.salary.toLocaleString()}
                        </span>{" "}
                        — {completedJob.result_summary.salary.label}{" "}
                        <span
                          className={`inline-block ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold ${
                            completedJob.result_summary.salary.confidence === "high"
                              ? "bg-emerald-200 text-emerald-900"
                              : completedJob.result_summary.salary.confidence === "medium"
                              ? "bg-amber-200 text-amber-900"
                              : "bg-red-200 text-red-900"
                          }`}
                        >
                          {completedJob.result_summary.salary.confidence} confidence
                        </span>
                      </div>
                      <div className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">
                        Source: {completedJob.result_summary.salary.source}
                        {completedJob.result_summary.salary.source_url &&
                          !completedJob.result_summary.salary.source.includes("http") && (
                            <>
                              {" "}
                              ·{" "}
                              <a
                                href={completedJob.result_summary.salary.source_url}
                                target="_blank"
                                rel="noreferrer"
                                className="underline"
                              >
                                link
                              </a>
                            </>
                          )}
                      </div>
                    </div>
                  )}
                  {completedJob.page_url && (
                    <div className="border-t border-emerald-200 dark:border-emerald-900 pt-3 mt-3">
                      <a
                        href={completedJob.page_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-300 hover:underline"
                      >
                        View page → {completedJob.page_url}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Error callout */}
              {trialFindingsError && (
                <div className="rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-4">
                  <div className="text-sm font-semibold text-red-800 dark:text-red-400 mb-1">
                    Generation failed
                  </div>
                  <div className="text-sm text-red-700 dark:text-red-400">{trialFindingsError}</div>
                </div>
              )}
            </div>
          );
        })()}
        {/* Delete Agency Tab */}
        {activeTab === "danger" && (
          <div className="space-y-6">
            <ArchiveAgencyPanel
              agency={agency}
              adminEmail={adminEmail}
              onChanged={async () => {
                modalRef.current?.close();
                setActiveTab("subscription");
                await onRefresh();
              }}
            />
            <DeleteAgencyPanel
              agency={agency}
              adminEmail={adminEmail}
              onDeleted={async () => {
                modalRef.current?.close();
                setActiveTab("subscription");
                await onRefresh();
              }}
            />
          </div>
        )}
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>close</button>
      </form>

      {/* Transfer Confirmation Modal */}
      <dialog className={`modal ${showTransferConfirmation ? "modal-open" : ""}`}>
        <div className="modal-box max-w-md">
          <h3 className="font-bold text-lg text-base-content mb-4">
            Confirm Agency Transfer
          </h3>
          <p className="text-gray-700 dark:text-base-content/80 mb-2">
            Send a transfer invitation for <strong>{agency.name}</strong> to:
          </p>
          <p className="text-lg font-semibold text-blue-600 dark:text-blue-300 mb-4">
            {newOwnerEmail}
          </p>
          <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md p-3 mb-4">
            <p className="text-sm text-red-700 dark:text-red-400">
              Once accepted, the new owner will become the primary administrator
              and the current owner ({agency.email}) will lose access.
            </p>
          </div>
          <div className="modal-action">
            <Button onClick={() => setShowTransferConfirmation(false)} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleSendTransfer} variant="primary">
              Yes, Send Invitation
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowTransferConfirmation(false)}>close</button>
        </form>
      </dialog>

      {/* Resend Confirmation Modal */}
      <dialog className={`modal ${showResendConfirmation ? "modal-open" : ""}`}>
        <div className="modal-box max-w-md">
          <h3 className="font-bold text-lg text-base-content mb-4">
            Resend Transfer Invitation
          </h3>
          <p className="text-gray-700 dark:text-base-content/80 mb-2">
            Resend the transfer invitation to:
          </p>
          <p className="text-lg font-semibold text-blue-600 dark:text-blue-300 mb-4">
            {pendingInvitation?.new_owner_email}
          </p>
          <p className="text-sm text-gray-600 dark:text-base-content/70 mb-4">
            This will generate a new invitation link and extend the expiration by 7 days.
          </p>
          <div className="modal-action">
            <Button onClick={() => setShowResendConfirmation(false)} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleResendInvitation} variant="primary">
              Yes, Resend
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowResendConfirmation(false)}>close</button>
        </form>
      </dialog>

      {/* Cancel Invitation Confirmation Modal */}
      <dialog className={`modal ${showCancelConfirmation ? "modal-open" : ""}`}>
        <div className="modal-box max-w-md">
          <h3 className="font-bold text-lg text-base-content mb-4">
            Cancel Transfer Invitation
          </h3>
          <p className="text-gray-700 dark:text-base-content/80 mb-2">
            Cancel the transfer invitation to:
          </p>
          <p className="text-lg font-semibold text-blue-600 dark:text-blue-300 mb-4">
            {pendingInvitation?.new_owner_email}
          </p>
          <div className="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-amber-900 rounded-md p-3 mb-4">
            <p className="text-sm text-yellow-800 dark:text-amber-400">
              This will invalidate the invitation link. You'll need to send a new
              invitation if you want to transfer later.
            </p>
          </div>
          <div className="modal-action">
            <Button onClick={() => setShowCancelConfirmation(false)} variant="secondary">
              Keep Invitation
            </Button>
            <Button onClick={handleCancelInvitation} variant="danger">
              Yes, Cancel Invitation
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowCancelConfirmation(false)}>close</button>
        </form>
      </dialog>
    </dialog>
  );
};

export default EditAgencyModal;
